<template>
    <div
        class="col-span-12 grid grid-cols-[auto_1fr] sm:grid-cols-subgrid relative items-center p-2 border-b-slate-200 border-b gap-x-4 gap-y-2 sm:gap-0"
        :class="[isDisabled ? 'bg-slate-100' : 'bg-white']"
        data-test="blast-forecast-row"
    >
        <div class="text-slate-500 text-xs sm:hidden">Messages:</div>
        <div class="sm:col-span-1">
            <vx-popover
                class="cursor-pointer"
                arrow
                placement="top"
                @click="
                    copyText(props.estimatedCost ? currencyCents(props.item.estimated_cost) : props.item.num_messages)
                "
                hover
                data-test="message-count"
            >
                <div
                    :class="[
                        'rounded-[4px] border-none px-2 min-h-6 text-xs flex items-center justify-center',
                        chartCalendarIntensities[
                            props.estimatedCost ? props.item.cost_intensity : props.item.intensity
                        ],
                        !props.estimatedCost && 'gap-1',
                    ]"
                >
                    <font-awesome-icon
                        class="size-2.5! -translate-y-[0.5px] opacity-60"
                        :icon="props.estimatedCost ? faDollar : faMessage"
                    ></font-awesome-icon>
                    {{
                        props.estimatedCost
                            ? vxFormatCentsAsDollars(props.item.estimated_cost, false)
                            : vxFormatCount(props.item.num_messages)
                    }}
                </div>
                <template v-slot:content>
                    <div
                        class="text-center"
                        @click="
                            copyText(
                                props.estimatedCost ? currencyCents(props.item.estimated_cost) : props.item.num_messages
                            )
                        "
                    >
                        {{
                            props.estimatedCost
                                ? currencyCents(props.item.estimated_cost)
                                : formatNumber(props.item.num_messages)
                        }}
                        <div class="text-xs pt-1 text-slate-500">Click to copy</div>
                    </div>
                </template>
            </vx-popover>
        </div>

        <div class="text-slate-500 text-xs sm:hidden">Date:</div>
        <div :class="[FEATURES.contact_groups ? 'sm:col-span-2' : 'sm:col-span-4']">
            <span data-test="date">{{ date.label }} </span>
            <span v-if="date.today" class="leading-none ml-2 inline sm:hidden md:inline">-> Today</span>
            <span v-else-if="date.sunday" class="leading-none ml-2 inline sm:hidden md:inline">-> Sun</span>
        </div>

        <template v-if="FEATURES.contact_groups">
            <div class="text-slate-500 text-xs sm:hidden">Senders:</div>
            <div class="sm:col-span-4">
                <div class="flex gap-1">
                    <meta-badge
                        v-if="props.item.num_corporate > 0"
                        :icon="faCrown"
                        color="amber"
                        data-test="corporate-badge"
                    >
                        {{ props.item.num_corporate }}
                    </meta-badge>
                    <meta-badge
                        v-if="props.item.num_org_units > 0"
                        :icon="faBuildingFlag"
                        color="violet"
                        data-test="org-units-badge"
                    >
                        {{ props.item.num_org_units }}
                    </meta-badge>
                    <meta-badge
                        v-if="props.item.num_groups > 0"
                        :icon="faGrid2Plus"
                        color="slate"
                        data-test="groups-badge"
                    >
                        {{ props.item.num_groups }}
                    </meta-badge>
                </div>
            </div>
        </template>

        <div class="text-slate-500 text-xs sm:hidden">Blasts:</div>
        <div class="sm:col-span-3">
            <vx-popover
                class="cursor-pointer"
                arrow
                placement="top"
                @click="copyText(props.item.num_blasts)"
                hover
                data-test="blast-count"
            >
                <div>
                    {{ vxFormatCount(props.item.num_blasts) }}
                    <span class="hidden sm:inline-block">{{ pluralized('blast', props.item.num_blasts, false) }}</span>
                </div>
                <template v-slot:content>
                    <div class="text-center" @click="copyText(props.item.num_blasts)">
                        {{ formatNumber(props.item.num_blasts) }}
                        <div class="text-xs pt-1 text-slate-500">Click to copy</div>
                    </div>
                </template>
            </vx-popover>
        </div>

        <div class="col-span-full sm:col-span-2 flex items-center justify-end gap-2">
            <vx-button
                color="muted-light"
                class="w-full sm:w-auto"
                :outline="showCampaigns"
                :disabled="isDisabled"
                size="xs"
                data-test="details-btn"
                @mouseenter="!showCampaigns && fetchCampaigns()"
                @click.stop="toggleCampaigns()"
            >
                Details
                <font-awesome-icon
                    :icon="faCaretDown"
                    class="w-4! h-4! transition-transform"
                    :class="{ 'rotate-180': showCampaigns }"
                />
            </vx-button>
        </div>
    </div>

    <div v-if="showCampaigns" class="pl-8 relative col-span-full bg-slate-100 border-b-slate-200 border-b">
        <div
            class="bg-sky-400 border-0! w-px absolute inset-bs-0 inset-s-4 ml-px"
            :class="[
                {
                    'h-full': props.nextItem,
                    'h-10': !props.nextItem,
                    'opacity-0': !nextItem && !showCampaigns,
                    transition: props.nextItem,
                },
            ]"
        ></div>

        <div class="py-[9px]">
            <vx-alert v-if="errorCampaigns" color="danger" :closeable="false" shadow>
                Failed to load data
                <template v-slot:right>
                    <vx-button @click="fetchCampaigns" :loading="loadingCampaigns" size="sm" color="danger"
                        >Retry<font-awesome-icon widthAuto :icon="faRefresh"
                    /></vx-button>
                </template>
            </vx-alert>
            <div v-else-if="loadingCampaigns" class="flex justify-center py-10">
                <vx-icon-loading class="w-5 h-5" spin />
            </div>
            <div v-else class="flex flex-col gap-4">
                <div v-for="(campaign, index) in campaigns" :key="campaign.id" class="relative">
                    <div
                        class="bg-sky-400 w-4 h-4 -mb-5 translate-y-9 -translate-x-[22.5px] inset-bs-10 rounded-full border-4! border-solid border-slate-100!"
                    ></div>

                    <blast-forecast-campaign-detail
                        v-model:campaign="campaigns[index]!"
                        @deleted="removeCampaign($event)"
                        @statusChanged="updateCampaignStatus"
                        :timezone="timezone"
                        :orgUnitId="props.orgUnitId"
                        :showEstimatedCost="props.showEstimatedCost"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import {
    chartCalendarIntensities,
    VxAlert,
    VxButton,
    vxFormatCentsAsDollars,
    vxFormatCount,
    VxIconLoading,
    VxPopover,
} from '@voxie/frontend-components';
import { pluralized } from '~/components/filters';
import BlastForecastCampaignDetail from './BlastForecastCampaignDetail.vue';
import MetaBadge from '~/components/general/MetaBadge.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    faCrown,
    faBuildingFlag,
    faGrid2Plus,
    faCaretDown,
    faRefresh,
    faMessage,
    faDollar,
} from '@fortawesome/pro-solid-svg-icons';
import dayjs, { toUTCDateTimeFormat } from '~/utils/dayjs';
import type {
    BlastForecastCampaignsResponse,
    GetBlastForecastRequest,
} from '~/services/blastForecast/blastForecastClient.types';
import blastForecastClient from '~/services/blastForecast/blastForecastClient';
import { computed, ref, watch, type PropType, inject } from 'vue';
import { useBlastForecastSummary, type BlastForecastItemAggregated } from '../composables/useBlastForecastSummary';
import { useCopyText } from '~/composables/useCopyText';
import type { Features } from '~/provides/features.types';
import type { CampaignStatus } from '~/constants/campaign';
import { currencyCents, formatNumber } from '~/utils/helpers';

const FEATURES = inject('FEATURES') as Features;

const teamId = Spark.state.currentTeam.id;

const props = defineProps({
    item: {
        type: Object as PropType<BlastForecastItemAggregated>,
        required: true,
    },
    timezone: String,
    fetchParams: {
        type: Object as PropType<GetBlastForecastRequest>,
        required: true,
    },
    nextItem: Object as PropType<BlastForecastItemAggregated>,
    orgUnitId: Number,
    showEstimatedCost: {
        type: Boolean,
        required: true,
    },
    estimatedCost: Boolean,
});

const isDisabled = computed(() => !props.item.num_blasts);

const { copyText } = useCopyText();
const { subtractCampaignFromDay } = useBlastForecastSummary();

const showCampaigns = ref(false);
const campaigns = ref<BlastForecastCampaignsResponse['items']>([]);

const removeCampaign = (campaignId: number) => {
    const campaign = campaigns.value.find((campaign) => campaign.id === campaignId);

    if (campaign) {
        subtractCampaignFromDay(props.item.date, campaign.sender.type, campaign.audience_size, campaign.estimated_cost);
    }

    campaigns.value = campaigns.value.filter((campaign) => campaign.id != campaignId);

    if (!campaigns.value.length) {
        showCampaigns.value = false;
    }
};

const updateCampaignStatus = (campaignId: number, newStatus: CampaignStatus) => {
    const campaign = campaigns.value.find((campaign) => campaign.id === campaignId);
    if (campaign) {
        campaign.status = newStatus;
    }
};

const errorCampaigns = ref(false);
const loadingCampaigns = ref(false);

let lastFetchedAt: dayjs.Dayjs | null = null;

const fetchCampaigns = async () => {
    if (loadingCampaigns.value) {
        return;
    }

    const now = dayjs();
    const oneMinuteAgo = now.subtract(1, 'minute');

    // prevents multiple api calls on mouseenter/multiple clicks
    if (!errorCampaigns.value && lastFetchedAt && lastFetchedAt.isAfter(oneMinuteAgo)) {
        return;
    }

    lastFetchedAt = now;

    loadingCampaigns.value = true;

    const dayStart = toUTCDateTimeFormat(dayjs.utc(props.item.date).tz(props.timezone).startOf('day'));
    const dayEnd = toUTCDateTimeFormat(
        dayjs.utc(props.item.date).tz(props.timezone).startOf('day').add(1, 'day').startOf('hour')
    );

    const dayParams: GetBlastForecastRequest = {
        ...props.fetchParams,
        range: [dayStart, dayEnd],
    };

    try {
        const response = await blastForecastClient.getBlastForecastCampaigns(
            { teamId, orgUnitId: props.orgUnitId },
            dayParams
        );
        campaigns.value = response.data.items;
        errorCampaigns.value = false;
    } catch (error) {
        console.error(`Error loading campaigns for ${props.item.date}:`, error);
        campaigns.value = [];
        errorCampaigns.value = true;
    } finally {
        loadingCampaigns.value = false;
    }
};

watch(showCampaigns, () => {
    if (showCampaigns.value) {
        fetchCampaigns();
    }
});

watch(
    () => props.fetchParams,
    () => {
        // closes day and clear the "cache" flag, so when day is opened the data is fetched again
        lastFetchedAt = null;
        showCampaigns.value = false;
    },
    {
        deep: true,
    }
);

const date = computed(() => {
    const dayjsInstance = dayjs.utc(props.item.date).tz(props.timezone);

    return {
        label: dayjsInstance.format('MMM D'),
        today: dayjsInstance.isToday(),
        sunday: dayjsInstance.day() === 0,
    };
});

const toggleCampaigns = () => {
    if (isDisabled.value) {
        return;
    }

    showCampaigns.value = !showCampaigns.value;
};

const openCampaigns = () => {
    if (isDisabled.value) {
        return;
    }

    showCampaigns.value = true;
};

const closeCampaigns = () => {
    if (isDisabled.value) {
        return;
    }

    showCampaigns.value = false;
};

const openCampaignsDeferred = () => {
    if (isDisabled.value) {
        return;
    }
    loadingCampaigns.value = true;
    showCampaigns.value = true;
};

const setCampaigns = (items: BlastForecastCampaignsResponse['items']) => {
    campaigns.value = items;
    loadingCampaigns.value = false;
    errorCampaigns.value = false;
    lastFetchedAt = dayjs();
};

const setCampaignsError = () => {
    campaigns.value = [];
    loadingCampaigns.value = false;
    errorCampaigns.value = true;
};

defineExpose({
    toggleCampaigns,
    openCampaigns,
    closeCampaigns,
    openCampaignsDeferred,
    setCampaigns,
    setCampaignsError,
});
</script>
